import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Spinner } from "reactstrap";
import { supportApi } from "../../services/AxiosInstance";
import "../Public/SupportPage.scss";

const initialState = () => ({
  email: "",
  phone: "",
  role: null,
  issue: null,
  problem: null,
  solutions: [],
  fallbackQuestions: [],
  fallbackAnswers: {},
  ticketDescription: "",
});

/** @typedef {null | 'role' | 'issue' | 'problem'} TicketShortcut */

function getRoleMeta(role) {
  const k = (role?.role_key || "").toLowerCase();
  if (k.includes("recruit")) {
    return { icon: "\uD83C\uDFE2", sub: "Managing jobs and team" };
  }
  return { icon: "\uD83D\uDC64", sub: "Looking for jobs or internships" };
}

const ISSUE_ICONS = ["\uD83D\uDCCB", "\u26A0\uFE0F", "\uD83D\uDCBC", "\u2699\uFE0F", "\uD83D\uDD14"];
const PROBLEM_ICONS = ["\u26A1", "\uD83D\uDD27", "\u2753", "\uD83D\uDCCC", "\uD83D\uDCE7"];

function normalizeSolutions(rawSolutions) {
  if (!Array.isArray(rawSolutions)) return [];

  const normalized = [];
  rawSolutions.forEach((item, parentIndex) => {
    const rawHtml = String(item?.solution_text || "").trim();
    if (!rawHtml) return;

    const hasListItems = /<li[\s>]/i.test(rawHtml);
    if (hasListItems && typeof window !== "undefined" && typeof DOMParser !== "undefined") {
      const doc = new DOMParser().parseFromString(rawHtml, "text/html");
      const liNodes = Array.from(doc.querySelectorAll("li"));

      if (liNodes.length > 0) {
        liNodes.forEach((li, liIndex) => {
          const liHtml = li.innerHTML.trim();
          if (!liHtml) return;

          normalized.push({
            ...item,
            solution_id: `${item?.solution_id || parentIndex}-${liIndex + 1}`,
            solution_text: liHtml,
            is_html: /<\/?[a-z][\s\S]*>/i.test(liHtml),
          });
        });
        return;
      }
    }

    normalized.push({
      ...item,
      solution_id: item?.solution_id || `${parentIndex + 1}`,
      solution_text: rawHtml,
      is_html: /<\/?[a-z][\s\S]*>/i.test(rawHtml),
    });
  });

  return normalized;
}

/** Keys stored in nf_support_problems.required_fields (JSON array, object, or comma list). */
const SUPPORT_PROBLEM_EXTRA_KEYS = ["used_email", "problem_page_url", "error_message"];

function parseProblemRequiredFields(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  const s = String(raw).trim();
  if (!s) return [];
  try {
    const d = JSON.parse(s);
    if (Array.isArray(d)) {
      return d.map((x) => String(x).trim()).filter(Boolean);
    }
    if (d && typeof d === "object") {
      return Object.keys(d).filter((k) => d[k]);
    }
  } catch {
    /* ignore */
  }
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}

function labelForProblemField(key) {
  switch (key) {
    case "used_email":
      return "Email used for this issue";
    case "problem_page_url":
      return "Problem page URL";
    case "error_message":
      return "Error message";
    default:
      return key;
  }
}

function ChevronRight() {
  return (
    <span className="sc-opt-arrow" aria-hidden>
      ›
    </span>
  );
}

function SearchIcon() {
  return (
    <svg
      className="support-center__search-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function TicketSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 0 0-2 2v3a2 2 0 1 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 1 1 0-4V7a2 2 0 0 0-2-2H5z" />
    </svg>
  );
}

function CheckSvg() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function RestartIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 .49-3.08" />
    </svg>
  );
}

/**
 * Shared support center UI. Use variant "public" for guests; jobseeker/recruiter inside dashboard layouts.
 * @param {{ variant?: "public" | "jobseeker" | "recruiter" }} props
 */
const AccountSupportCenter = ({ variant = "public" }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialState);
  const [roles, setRoles] = useState([]);
  const [issues, setIssues] = useState([]);
  const [problems, setProblems] = useState([]);
  const [problemSearch, setProblemSearch] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  /** @type {[TicketShortcut, function]} */
  const [ticketShortcut, setTicketShortcut] = useState(null);
  const [solReveal, setSolReveal] = useState(0);
  const [showSolutionFeedback, setShowSolutionFeedback] = useState(false);
  const [toast, setToast] = useState("");
  const [ticketSuccess, setTicketSuccess] = useState(null);
  const [resolvedNoTicket, setResolvedNoTicket] = useState(false);
  /** Extra ticket inputs from nf_support_problems.required_fields (when user picked a problem). */
  const [problemFieldValues, setProblemFieldValues] = useState({});

  const toastTimerRef = useRef(null);
  const fallbackLoadedRef = useRef(false);

  const authSnapshot = useMemo(() => {
    let token = "";
    let userData = null;
    try {
      token = String(localStorage.getItem("_token") || "");
      userData = JSON.parse(localStorage.getItem("user_data") || "null");
    } catch (_) {
      token = "";
      userData = null;
    }
    return { token, userData };
  }, []);

  const isLoggedInUser = Boolean(authSnapshot.token);
  const loginType = useMemo(() => {
    if (variant === "jobseeker" || variant === "recruiter") {
      return variant;
    }
    return String(authSnapshot.userData?.type || "")
      .trim()
      .toLowerCase();
  }, [variant, authSnapshot.userData]);
  const matchedLoginRole = useMemo(() => {
    if (!isLoggedInUser || !loginType || !Array.isArray(roles) || roles.length === 0) {
      return null;
    }
    return (
      roles.find((r) => {
        const roleKey = String(r?.role_key || "")
          .trim()
          .toLowerCase();
        return roleKey === loginType || roleKey.includes(loginType);
      }) || null
    );
  }, [isLoggedInUser, loginType, roles]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(""), 3200);
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetFlow = () => {
    const base = initialState();
    if (isLoggedInUser && matchedLoginRole) {
      base.role = matchedLoginRole;
      setStep(2);
    } else {
      setStep(1);
    }
    setForm(base);
    setError("");
    setTicketShortcut(null);
    setIssues([]);
    setProblems([]);
    setProblemSearch("");
    setAttachmentName("");
    setAttachmentFile(null);
    setSolReveal(0);
    setShowSolutionFeedback(false);
    setTicketSuccess(null);
    setResolvedNoTicket(false);
    setToast("");
    setProblemFieldValues({});
    fallbackLoadedRef.current = false;
  };

  const loadSolutions = useCallback(async (problemId) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await supportApi.getSolutions(problemId);
      if (!data?.status) throw new Error(data?.message || "Failed to load solutions");
      return normalizeSolutions(data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Could not load solutions");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFallbackQuestionsSilent = useCallback(async () => {
    try {
      const { data } = await supportApi.getFallbackQuestions();
      if (!data?.status) return null;
      return data.data || [];
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (roles.length > 0) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await supportApi.getRoles();
        if (!data?.status) throw new Error(data?.message || "Failed to load roles");
        if (!cancelled) setRoles(data.data || []);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || "Could not load roles");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roles.length]);

  useEffect(() => {
    if (!form.role) return;
    if (step !== 2 && step !== 5) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await supportApi.getIssues(form.role.role_id);
        if (!data?.status) throw new Error(data?.message || "Failed to load");
        if (!cancelled) setIssues(data.data || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.role?.role_id, step]);

  useEffect(() => {
    if (!form.issue) return;
    if (step !== 3 && step !== 5) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await supportApi.getProblems(form.issue.issue_id);
        if (!data?.status) throw new Error(data?.message || "Failed to load");
        if (!cancelled) setProblems(data.data || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.issue?.issue_id, step]);

  useEffect(() => {
    if (step !== 5) {
      fallbackLoadedRef.current = false;
      return;
    }
    if (fallbackLoadedRef.current) return;
    fallbackLoadedRef.current = true;
    let cancelled = false;
    (async () => {
      const qs = await loadFallbackQuestionsSilent();
      if (cancelled || !qs?.length) return;
      setForm((p) => {
        const next = { ...p, fallbackQuestions: qs };
        qs.forEach((q) => {
          if (next.fallbackAnswers[q.question_id] === undefined) {
            next.fallbackAnswers[q.question_id] = "";
          }
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [step, loadFallbackQuestionsSilent]);

  const ticketExtraFieldKeys = useMemo(() => {
    if (!form.problem) return [];
    const req = parseProblemRequiredFields(form.problem.required_fields);
    return SUPPORT_PROBLEM_EXTRA_KEYS.filter((k) => req.includes(k));
  }, [form.problem?.problem_id, form.problem?.required_fields]);

  useEffect(() => {
    if (step !== 5) return;
    if (ticketExtraFieldKeys.length === 0) {
      setProblemFieldValues({});
      return;
    }
    setProblemFieldValues((prev) => {
      const next = {};
      ticketExtraFieldKeys.forEach((k) => {
        const existing = prev[k];
        if (existing !== undefined && String(existing).trim() !== "") {
          next[k] = existing;
        } else if (k === "problem_page_url") {
          next[k] = typeof window !== "undefined" ? window.location.href : "";
        } else {
          next[k] = "";
        }
      });
      return next;
    });
  }, [step, form.problem?.problem_id, ticketExtraFieldKeys.join("\u0000")]);

  useEffect(() => {
    if (step !== 4) {
      setSolReveal(0);
      setShowSolutionFeedback(false);
      return;
    }
    const list = form.solutions || [];
    const n = list.length;
    setSolReveal(0);
    setShowSolutionFeedback(false);

    if (n === 0) {
      const t = window.setTimeout(() => setShowSolutionFeedback(true), 400);
      return () => clearTimeout(t);
    }

    let reveal = 0;
    const timers = [];
    const tick = () => {
      reveal += 1;
      setSolReveal(reveal);
      if (reveal < n) {
        timers.push(window.setTimeout(tick, 500));
      } else {
        timers.push(window.setTimeout(() => setShowSolutionFeedback(true), 600));
      }
    };
    timers.push(window.setTimeout(tick, 400));
    return () => timers.forEach(clearTimeout);
  }, [step, form.problem?.problem_id, form.solutions]);

  const crumbs = useMemo(() => {
    const out = [];
    if (form.role && step >= 2) {
      out.push({
        id: "role",
        label: form.role.role_label || form.role.role_key,
        jump: "role",
      });
    }
    if (form.issue && step >= 3) {
      out.push({
        id: "issue",
        label: form.issue.issue_label || form.issue.issue_key,
        jump: "issue",
      });
    }
    if (form.problem && step >= 4) {
      out.push({
        id: "problem",
        label: form.problem.problem_label || form.problem.problem_key,
        jump: "problem",
      });
    }
    return out;
  }, [form.role, form.issue, form.problem, step]);

  const crumbActiveIndex = crumbs.length - 1;

  const jumpToCrumb = (index) => {
    setError("");
    setTicketShortcut(null);
    setAttachmentName("");
    setAttachmentFile(null);
    setProblemSearch("");
    if (index === 0) {
      const base = initialState();
      if (isLoggedInUser && matchedLoginRole) {
        base.role = matchedLoginRole;
        setStep(2);
      } else {
        setStep(1);
      }
      setForm(base);
      setIssues([]);
      setProblems([]);
      setProblemFieldValues({});
      return;
    }
    if (index === 1) {
      setForm((p) => ({
        ...p,
        issue: null,
        problem: null,
        solutions: [],
        fallbackQuestions: [],
        fallbackAnswers: {},
        ticketDescription: "",
      }));
      setProblems([]);
      setProblemFieldValues({});
      setStep(2);
      return;
    }
    if (index === 2) {
      setForm((p) => ({
        ...p,
        problem: null,
        solutions: [],
        fallbackQuestions: [],
        fallbackAnswers: {},
        ticketDescription: "",
      }));
      setProblemFieldValues({});
      setStep(3);
    }
  };

  const goBack = () => {
    setError("");
    if (step === 5) {
      if (ticketShortcut === "role") {
        setTicketShortcut(null);
        const base = initialState();
        if (isLoggedInUser && matchedLoginRole) {
          base.role = matchedLoginRole;
          setStep(2);
        } else {
          setStep(1);
        }
        setForm(base);
        setIssues([]);
        setProblems([]);
        setProblemFieldValues({});
        return;
      }
      if (ticketShortcut === "issue") {
        setTicketShortcut(null);
        setProblems([]);
        setProblemFieldValues({});
        setStep(2);
        return;
      }
      if (ticketShortcut === "problem") {
        setTicketShortcut(null);
        setProblemFieldValues({});
        setStep(3);
        return;
      }
      setStep(4);
      setShowSolutionFeedback(true);
      return;
    }
    if (step === 4) {
      setStep(3);
      setForm((p) => ({ ...p, problem: null, solutions: [] }));
      return;
    }
    if (step === 3) {
      setStep(2);
      setForm((p) => ({
        ...p,
        issue: null,
        problem: null,
        solutions: [],
      }));
      setProblems([]);
      return;
    }
    if (step === 2) {
      if (isLoggedInUser && matchedLoginRole) {
        return;
      }
      setStep(1);
      setForm((p) => ({
        ...p,
        role: null,
        issue: null,
        problem: null,
        solutions: [],
      }));
      setIssues([]);
      setProblems([]);
      setProblemFieldValues({});
    }
  };

  const onSelectRole = (role) => {
    setError("");
    setForm((p) => ({
      ...p,
      role,
      issue: null,
      problem: null,
      solutions: [],
    }));
    setIssues([]);
    setProblems([]);
    setProblemFieldValues({});
    setStep(2);
  };

  useEffect(() => {
    if (!isLoggedInUser || !matchedLoginRole) return;

    const currentRoleId = String(form.role?.role_id || "");
    const targetRoleId = String(matchedLoginRole.role_id || "");
    if (currentRoleId !== targetRoleId) {
      setForm((p) => ({
        ...p,
        role: matchedLoginRole,
        issue: null,
        problem: null,
        solutions: [],
      }));
      setIssues([]);
      setProblems([]);
      setProblemSearch("");
      setProblemFieldValues({});
    }

    if (step === 1) {
      setStep(2);
    }
  }, [isLoggedInUser, matchedLoginRole, form.role?.role_id, step]);

  useEffect(() => {
    let token = "";
    let userData = null;
    try {
      token = String(localStorage.getItem("_token") || "");
      userData = JSON.parse(localStorage.getItem("user_data") || "null");
    } catch (_) {
      token = "";
      userData = null;
    }

    if (!token || !userData) return;

    const guessedEmail = String(
      userData?.email ||
        userData?.jobseeker_email1 ||
        userData?.recruiter_email ||
        userData?.admin_email ||
        ""
    ).trim();
    const guessedPhone = String(
      userData?.phone ||
        userData?.jobseeker_phone1 ||
        userData?.recruiter_mobile_number ||
        userData?.recruiter_phone ||
        userData?.mobile ||
        ""
    ).trim();

    if (!guessedEmail && !guessedPhone) return;

    setForm((prev) => ({
      ...prev,
      email: prev.email || guessedEmail,
      phone: prev.phone || guessedPhone,
    }));
  }, []);

  const onSelectIssue = (issue) => {
    setError("");
    setForm((p) => ({
      ...p,
      issue,
      problem: null,
      solutions: [],
    }));
    setProblems([]);
    setProblemSearch("");
    setProblemFieldValues({});
    setStep(3);
  };

  const onSelectProblem = async (problem) => {
    setProblemFieldValues({});
    setForm((p) => ({ ...p, problem, solutions: [] }));
    const solutions = await loadSolutions(problem.problem_id);
    if (solutions) {
      setForm((p) => ({ ...p, problem, solutions }));
      setStep(4);
    }
  };

  const openFallbackFromIssue = () => {
    setError("");
    setTicketShortcut("issue");
    setForm((p) => ({
      ...p,
      issue: null,
      problem: null,
      solutions: [],
      fallbackQuestions: [],
      fallbackAnswers: {},
      ticketDescription: "",
    }));
    setProblems([]);
    setProblemFieldValues({});
    setStep(5);
    fallbackLoadedRef.current = false;
  };

  const openFallbackFromProblem = () => {
    setError("");
    setTicketShortcut("problem");
    setForm((p) => ({
      ...p,
      problem: null,
      solutions: [],
      fallbackQuestions: [],
      fallbackAnswers: {},
      ticketDescription: "",
    }));
    setProblemFieldValues({});
    setStep(5);
    fallbackLoadedRef.current = false;
  };

  const onResolvedYes = () => {
    showToast("Awesome! Glad we could help.");
    window.setTimeout(() => setResolvedNoTicket(true), 320);
  };

  const onResolvedNo = async () => {
    setTicketShortcut(null);
    const qs = await loadFallbackQuestionsSilent();
    if (qs?.length) {
      setForm((p) => {
        const next = { ...p, fallbackQuestions: qs };
        qs.forEach((q) => {
          next.fallbackAnswers[q.question_id] = p.fallbackAnswers[q.question_id] || "";
        });
        return next;
      });
      fallbackLoadedRef.current = true;
    } else {
      fallbackLoadedRef.current = false;
    }
    setStep(5);
  };

  const onSubmitTicket = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.role) {
      showToast("Please choose role.");
      return;
    }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      showToast("Please enter a valid email address.");
      return;
    }
    if (!form.phone.trim()) {
      showToast("Please fill in your phone number.");
      return;
    }
    if (!form.ticketDescription.trim()) {
      showToast("Please describe your issue briefly.");
      return;
    }

    const extraKeys =
      step === 5 && form.problem ? ticketExtraFieldKeys : [];
    for (let i = 0; i < extraKeys.length; i += 1) {
      const k = extraKeys[i];
      const v = String(problemFieldValues[k] || "").trim();
      if (!v) {
        showToast(`Please fill in: ${labelForProblemField(k)}`);
        return;
      }
      if (k === "used_email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        showToast("Please enter a valid email for the account email field.");
        return;
      }
      if (k === "problem_page_url") {
        try {
          const u = new URL(v);
          if (!/^https?:$/i.test(u.protocol)) {
            throw new Error("invalid");
          }
        } catch {
          showToast("Please enter a valid problem page URL (including https://).");
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      let userData = null;
      try {
        userData = JSON.parse(localStorage.getItem("user_data") || "null");
      } catch (_) {
        userData = null;
      }

      const loggedInUserId = String(
        userData?.user_id || userData?.recruiter_id || userData?.seeker_id || userData?.jobseeker_id || ""
      ).trim();

      const meta = (form.fallbackQuestions || []).map((q) => ({
        question: q.question_text || "",
        answer: String(form.fallbackAnswers[q.question_id] || "").trim(),
      }));

      const payload = new FormData();
      if (loggedInUserId) {
        payload.append("user_id", loggedInUserId);
      }
      payload.append("role_id", String(form.role.role_id));
      if (form.issue?.issue_id) {
        payload.append("issue_id", String(form.issue.issue_id));
      }
      if (form.problem?.problem_id) {
        payload.append("problem_id", String(form.problem.problem_id));
      }
      payload.append("email", form.email.trim());
      payload.append("phone", form.phone.trim());
      payload.append("description", form.ticketDescription.trim());
      payload.append("meta", JSON.stringify(meta));
      if (extraKeys.length > 0) {
        payload.append(
          "problem_fields",
          JSON.stringify(
            Object.fromEntries(extraKeys.map((k) => [k, String(problemFieldValues[k] || "").trim()]))
          )
        );
      }
      if (attachmentFile) {
        payload.append("screenshot", attachmentFile);
      }

      const { data } = await supportApi.createTicket(payload);

      if (!data?.status) {
        throw new Error(data?.message || "Could not create ticket");
      }
      setTicketSuccess(data.ticket_id);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to submit ticket"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabel = useMemo(() => {
    const m = {
      1: "Step 1 — Your role",
      2: "Step 2 — Category",
      3: "Step 3 — Problem",
      4: "Step 4 — Solutions",
      5: "Raise ticket",
    };
    return m[step] || "";
  }, [step]);

  const stepQuestion = useMemo(() => {
    const m = {
      1: "No waiting — pick the option that fits you best.",
      2: "What's the issue about?",
      3: "Which one describes it best?",
      4: "Try these fixes one by one —",
      5: "Tell us what's going on —",
    };
    return m[step] || "";
  }, [step]);

  const filteredProblems = useMemo(() => {
    const q = problemSearch.trim().toLowerCase();
    if (!q) return problems;
    return problems.filter((p) => {
      const label = (p.problem_label || p.problem_key || "").toLowerCase();
      return label.includes(q);
    });
  }, [problems, problemSearch]);

  /** Role / category / problem picked earlier in the flow — shown on the ticket step */
  const ticketAutoContextLine = useMemo(() => {
    const roleL = form.role
      ? String(form.role.role_label || form.role.role_key || "").trim()
      : "";
    const issueL = form.issue
      ? String(form.issue.issue_label || form.issue.issue_key || "").trim()
      : "";
    const probL = form.problem
      ? String(form.problem.problem_label || form.problem.problem_key || "").trim()
      : "";
    if (!roleL && !issueL && !probL) return "";
    const segments = [];
    if (roleL) segments.push(`Role: ${roleL}`);
    if (issueL) segments.push(`Category: ${issueL}`);
    if (probL) segments.push(`Problem: ${probL}`);
    return segments.join(" · ");
  }, [form.role, form.issue, form.problem]);

  const showGreeting = step === 1 && !isLoggedInUser;
  const nSolutions = form.solutions?.length || 0;
  const showSolTyping =
    step === 4 && nSolutions > 0 && solReveal > 0 && solReveal < nSolutions;
  const selectedRoleLabel = form.role ? form.role.role_label || form.role.role_key : "";

  const supportRootClassName = `support-center${
    variant !== "public" ? " support-center--dashboard" : ""
  }`;

  const needRoleSelect = step === 5 && !form.role;
  if (resolvedNoTicket) {
    return (
      <div className={supportRootClassName}>
        <div className="support-center__inner">
          <div className="support-center__success">
            <div className="support-center__success-icon">
              <CheckSvg />
            </div>
            <h2>Happy to help!</h2>
            <p>
              We&apos;re glad the steps worked. If you run into anything else, we&apos;re
              here.
            </p>
            <div className="support-center__ticket-ref">Issue resolved</div>
            <button type="button" className="support-center__restart-btn" onClick={resetFlow}>
              <RestartIcon />
              Start over
            </button>
          </div>
        </div>
        <div
          className={`support-center__toast ${toast ? "support-center__toast--show" : ""}`}
        >
          {toast}
        </div>
      </div>
    );
  }

  if (ticketSuccess != null) {
    return (
      <div className={supportRootClassName}>
        <div className="support-center__inner">
          <div className="support-center__success">
            <div className="support-center__success-icon">
              <CheckSvg />
            </div>
            <h2>Ticket raised!</h2>
            <p>
              We&apos;ve received your request and our support team will reach out to your
              registered email or phone very soon.
            </p>
            <div className="support-center__ticket-ref">#{ticketSuccess}</div>
            <button type="button" className="support-center__restart-btn" onClick={resetFlow}>
              <RestartIcon />
              Start over
            </button>
          </div>
        </div>
      </div>
    );
  }

  const headerActions = (
    <>
      {step > 1 && (
        <button type="button" className="support-center__btn-ghost" onClick={goBack}>
          Back
        </button>
      )}
      {/* <button type="button" className="support-center__btn-link" onClick={resetFlow}>
        Start over
      </button> */}
    </>
  );

  return (
    <Fragment>
      <div className={supportRootClassName}>
        <div className="support-center__inner">
          <div className="support-center__top">
            <span className="support-center__header-badge">Support Center</span>
            <div className="support-center__actions">{headerActions}</div>
          </div>

          {showGreeting && (
            <div className="support-center__greeting">
              <h1>
                Hey there! Let&apos;s fix
                <br />
                this <em>together.</em>
              </h1>
              <p>
                No waiting, no bots — just a quick path to your answer. Tell us a bit about
                your role.
              </p>
            </div>
          )}

          {crumbs.length > 0 && step <= 4 && (
            <div className="support-center__breadcrumb">
              {crumbs.map((c, i) => (
                <Fragment key={c.id}>
                  {i > 0 && <span className="sc-crumb-sep">›</span>}
                  <button
                    type="button"
                    className={`sc-crumb ${
                      i === crumbActiveIndex ? "sc-crumb--active" : "sc-crumb--done"
                    }`}
                    style={{ animationDelay: `${i * 0.04}s` }}
                    onClick={() => jumpToCrumb(i)}
                  >
                    {c.label}
                  </button>
                </Fragment>
              ))}
            </div>
          )}

          {error && (
            <Alert color="danger" className="support-center__alert">
              {error}
            </Alert>
          )}

          {step <= 4 && (
            <>
              <div className="support-center__step-label">{stepLabel}</div>
              <div className="support-center__step-question">{stepQuestion}</div>
              {(step === 2 || step === 3) && selectedRoleLabel && (
                <div className="support-center__step-subtitle">{`Role: ${selectedRoleLabel}`}</div>
              )}
            </>
          )}

          {loading && (step === 1 || step === 2) && (
            <div className="support-center__typing" aria-live="polite">
              <span />
              <span />
              <span />
            </div>
          )}

          {step === 1 && loading && roles.length === 0 ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : null}

          {step === 1 && !(loading && roles.length === 0) && (
            <div className={`support-center__options-grid support-center__role-grid`}>
              {roles.map((r, i) => {
                const meta = getRoleMeta(r);
                return (
                  <button
                    key={r.role_id}
                    type="button"
                    className="sc-opt-card sc-opt-card--role sc-opt-card--animate"
                    style={{ animationDelay: `${i * 0.07}s` }}
                    onClick={() => onSelectRole(r)}
                  >
                    <span className="sc-opt-icon">{meta.icon}</span>
                    <div className="sc-opt-body">
                      <div className="sc-opt-title">{r.role_label || r.role_key}</div>
                      <div className="sc-opt-sub">{meta.sub}</div>
                    </div>
                    <ChevronRight />
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && form.role && (
            <div className={`support-center__options-grid support-center__issue-grid`}>
              {loading && issues.length === 0 ? (
                <div className="text-center py-4 w-100">
                  <Spinner color="primary" />
                </div>
              ) : (
                issues.map((issue, idx) => (
                  <button
                    key={issue.issue_id}
                    type="button"
                    className="sc-opt-card sc-opt-card--animate"
                    style={{ animationDelay: `${idx * 0.07}s` }}
                    onClick={() => onSelectIssue(issue)}
                  >
                    <span className="sc-opt-icon">{ISSUE_ICONS[idx % ISSUE_ICONS.length]}</span>
                    <div className="sc-opt-body">
                      <div className="sc-opt-title">
                        {issue.issue_label || issue.issue_key}
                      </div>
                    </div>
                    <ChevronRight />
                  </button>
                ))
              )}
              {!loading && issues.length > 0 && (
                <button
                  type="button"
                  className="sc-opt-card sc-opt-card--animate"
                  style={{ animationDelay: `${issues.length * 0.07}s` }}
                  onClick={openFallbackFromIssue}
                >
                  <span className="sc-opt-icon">{"\u2795"}</span>
                  <div className="sc-opt-body">
                    <div className="sc-opt-title">Something else</div>
                  </div>
                  <ChevronRight />
                </button>
              )}
            </div>
          )}

          {step === 3 && form.issue && (
            <>
              <div className="support-center__search-wrap">
                <SearchIcon />
                <input
                  type="search"
                  className="support-center__search-input"
                  placeholder="Search your problem..."
                  value={problemSearch}
                  onChange={(e) => setProblemSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {loading && problems.length === 0 ? (
                <div className="support-center__typing">
                  <span />
                  <span />
                  <span />
                </div>
              ) : (
                <div className="support-center__options-grid">
                  {filteredProblems.map((problem, idx) => (
                    <button
                      key={problem.problem_id}
                      type="button"
                      className="sc-opt-card sc-opt-card--animate"
                      style={{ animationDelay: `${idx * 0.06}s` }}
                      onClick={() => onSelectProblem(problem)}
                    >
                      <span className="sc-opt-icon">
                        {PROBLEM_ICONS[idx % PROBLEM_ICONS.length]}
                      </span>
                      <div className="sc-opt-body">
                        <div className="sc-opt-title">
                          {problem.problem_label || problem.problem_key}
                        </div>
                      </div>
                      <ChevronRight />
                    </button>
                  ))}
                  <button
                    type="button"
                    className="sc-opt-card sc-opt-card--animate"
                    style={{ animationDelay: `${filteredProblems.length * 0.06}s` }}
                    onClick={openFallbackFromProblem}
                  >
                    <span className="sc-opt-icon">{"\uD83D\uDCAC"}</span>
                    <div className="sc-opt-body">
                      <div className="sc-opt-title">None of these match</div>
                    </div>
                    <ChevronRight />
                  </button>
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <p className="support-center__sol-header">Here&apos;s what might help —</p>
              {nSolutions === 0 && (
                <p className="text-muted small">
                  No written steps yet. Use the buttons below to continue.
                </p>
              )}
              {form.solutions.slice(0, solReveal).map((s, i) => {
                const raw = s.solution_text || "";
                const looksHtml = Boolean(s.is_html);
                return (
                  <div
                    key={s.solution_id || i}
                    className="sc-sol-item sc-opt-card--animate"
                  >
                    <div className="sc-sol-num">{i + 1}</div>
                    {looksHtml ? (
                      <div
                        className="sc-sol-text"
                        dangerouslySetInnerHTML={{ __html: raw }}
                      />
                    ) : (
                      <div className="sc-sol-text" style={{ whiteSpace: "pre-wrap" }}>
                        {raw}
                      </div>
                    )}
                  </div>
                );
              })}
              {showSolTyping && (
                <div className="support-center__sol-typing" aria-hidden>
                  <span />
                  <span />
                  <span />
                </div>
              )}
              {showSolutionFeedback && (
                <div className="support-center__feedback">
                  <p className="support-center__feedback-q">
                    Did any of these solutions fix your issue?
                  </p>
                  <div className="support-center__feedback-btns">
                    <button type="button" className="sc-fbtn sc-fbtn-yes" onClick={onResolvedYes}>
                      {`Yes, resolved! ${"\uD83C\uDF89"}`}
                    </button>
                    <button type="button" className="sc-fbtn sc-fbtn-no" onClick={onResolvedNo}>
                      No, still facing issue
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 5 && (
            <div className="support-center__ticket">
              <div className="support-center__step-label">{stepLabel}</div>
              <div className="support-center__step-question">{stepQuestion}</div>

              <div className="support-center__ticket-header">
                <div className="support-center__ticket-icon">
                  <TicketSvg />
                </div>
                <div>
                  <div className="support-center__ticket-title">Raise a support ticket</div>
                  <div className="support-center__ticket-sub">
                    Our team will get back to you shortly.
                  </div>
                </div>
              </div>

              {ticketAutoContextLine ? (
                <div
                  className="support-center__context-box support-center__context-box--auto"
                  role="status"
                  aria-live="polite"
                >
                  <strong>Context captured automatically</strong>
                  <div className="support-center__context-box-line">{ticketAutoContextLine}</div>
                </div>
              ) : selectedRoleLabel ? (
                <div className="support-center__step-subtitle">{`Role: ${selectedRoleLabel}`}</div>
              ) : null}

              {needRoleSelect && (
                <div className="mb-3">
                  <label className="sc-form-label">
                    Your role <span>*</span>
                  </label>
                  <select
                    className="sc-form-input"
                    value={form.role?.role_id ?? ""}
                    onChange={(e) => {
                      const id = String(e.target.value);
                      const r = roles.find((x) => String(x.role_id) === id) || null;
                      setForm((p) => ({
                        ...p,
                        role: r,
                        issue: null,
                        problem: null,
                      }));
                      setIssues([]);
                      setProblems([]);
                    }}
                  >
                    <option value="">Select role...</option>
                    {roles.map((r) => (
                      <option key={r.role_id} value={r.role_id}>
                        {r.role_label || r.role_key}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <form onSubmit={onSubmitTicket}>
                <div className="sc-form-grid" style={{ marginBottom: "1rem" }}>
                  <div>
                    <label className="sc-form-label" htmlFor="t-email">
                      Email <span>*</span>
                    </label>
                    <input
                      id="t-email"
                      type="email"
                      className="sc-form-input"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="you@email.com"
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label className="sc-form-label" htmlFor="t-phone">
                      Phone <span>*</span>
                    </label>
                    <input
                      id="t-phone"
                      type="tel"
                      className="sc-form-input"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                      autoComplete="tel"
                    />
                  </div>
                </div>

                {ticketExtraFieldKeys.map((k) => (
                  <div key={k} style={{ marginBottom: "1rem" }}>
                    <label className="sc-form-label" htmlFor={`t-pf-${k}`}>
                      {labelForProblemField(k)} <span>*</span>
                    </label>
                    {k === "error_message" ? (
                      <textarea
                        id={`t-pf-${k}`}
                        className="sc-form-textarea"
                        rows={3}
                        value={problemFieldValues[k] || ""}
                        onChange={(e) =>
                          setProblemFieldValues((p) => ({ ...p, [k]: e.target.value }))
                        }
                        placeholder={
                          k === "error_message"
                            ? "Paste the exact message or code you see..."
                            : ""
                        }
                      />
                    ) : (
                      <input
                        id={`t-pf-${k}`}
                        type={k === "used_email" ? "email" : "url"}
                        className="sc-form-input"
                        value={problemFieldValues[k] || ""}
                        onChange={(e) =>
                          setProblemFieldValues((p) => ({ ...p, [k]: e.target.value }))
                        }
                        placeholder={
                          k === "used_email"
                            ? "Account email where the issue occurred"
                            : "https://..."
                        }
                        autoComplete={k === "used_email" ? "email" : "off"}
                      />
                    )}
                  </div>
                ))}

                {(form.fallbackQuestions || []).map((q) => (
                  <div key={q.question_id} style={{ marginBottom: "1rem" }}>
                    <label className="sc-form-label">{q.question_text}</label>
                    <textarea
                      className="sc-form-textarea"
                      rows={2}
                      value={form.fallbackAnswers[q.question_id] || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          fallbackAnswers: {
                            ...p.fallbackAnswers,
                            [q.question_id]: e.target.value,
                          },
                        }))
                      }
                      placeholder="Type here..."
                    />
                  </div>
                ))}

                <div style={{ marginBottom: "1rem" }}>
                  <label className="sc-form-label" htmlFor="t-desc">
                    Describe your issue <span>*</span>
                  </label>
                  <textarea
                    id="t-desc"
                    className="sc-form-textarea"
                    rows={4}
                    value={form.ticketDescription}
                    onChange={(e) => setField("ticketDescription", e.target.value)}
                    placeholder="Tell us what happened in detail..."
                  />
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <span className="sc-form-label">Attachment (optional)</span>
                  <label className="sc-file-wrap">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          const allowedExts = ["jpg", "jpeg", "png", "gif", "webp", "pdf", "doc", "docx", "txt"];
                          const ext = f.name.split(".").pop().toLowerCase();
                          if (!allowedExts.includes(ext)) {
                            showToast("Invalid file format! Allowed: Images, PDF, DOC, DOCX, TXT.");
                            e.target.value = "";
                            setAttachmentFile(null);
                            setAttachmentName("");
                            return;
                          }
                          if (f.size > 5 * 1024 * 1024) {
                            showToast("File size exceeds 5MB limit.");
                            e.target.value = "";
                            setAttachmentFile(null);
                            setAttachmentName("");
                            return;
                          }
                        }
                        setAttachmentFile(f || null);
                        setAttachmentName(f ? f.name : "");
                      }}
                    />
                    <div className="sc-file-text">
                      <strong>Click to upload</strong> screenshot or file
                    </div>
                    {attachmentName && (
                      <div className="small mt-1" style={{ color: "var(--sc-accent-deep)" }}>
                        {attachmentName}
                      </div>
                    )}
                  </label>
                </div>

                <button
                  type="submit"
                  className="support-center__submit-btn"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit ticket →"}
                </button>
              </form>
            </div>
          )}
        </div>

        <div
          className={`support-center__toast ${toast ? "support-center__toast--show" : ""}`}
        >
          {toast}
        </div>
      </div>
    </Fragment>
  );
};

export default AccountSupportCenter;
